import { isLink } from 'shared/lib'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type AttachmentStore } from 'entities/Attachment/store/attachmentStore'

/**
 * Shared logic for reconstructing non-media attachments (documents / audio) from
 * the short links that live in a message body. Such attachments are not stored
 * in `media_url` (which holds MMS-eligible media only), so they are resolved by
 * requesting preview metadata for the body links through the attachment store.
 *
 * Shared by the Survey (`reconstructSurveyAttachments`) and Broadcast
 * (`BroadcastSMSStore`) reload flows.
 */

// Content types that are NOT rendered as MMS media (shown as text links instead).
export const NOT_ALLOWED_FOR_MEDIA_TYPES = [
  // Documents
  'application/pdf',
  'text/csv',
  'text/plain',
  'text/markdown',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // Audio
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/ogg',
  // Videos
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
]

// Content types that are never valid attachments (regular links / web pages).
export const INVALID_PREVIEW_CONTENT_TYPES = ['text/html', 'application/xhtml+xml']

// Delays (ms) to wait for the preview request to settle before reading results.
export const PREVIEW_RESOLVE_DELAYS_MS = [300, 500]

export const determineIsAllowedForMediaUrl = (contentType: string): boolean => {
  if (NOT_ALLOWED_FOR_MEDIA_TYPES.includes(contentType)) return false
  if (contentType.startsWith('video/') || contentType.startsWith('audio/')) return false
  return contentType.startsWith('image/')
}

export const isValidPreviewAttachment = (
  attachment: Attachment | null | undefined
): attachment is Attachment => {
  if (!attachment) return false
  if (INVALID_PREVIEW_CONTENT_TYPES.includes(attachment.content_type)) return false
  return Boolean(attachment.source)
}

export const extractLinksFromText = (text: string): string[] => {
  const links: string[] = []
  text.split(/\n/).forEach((line) =>
    line.split(' ').forEach((word) => {
      if (isLink(word)) links.push(word)
    })
  )
  return links
}

const buildAttachmentFromLink = (
  attachmentStore: AttachmentStore,
  url: string
): Attachment | null => {
  const previous = attachmentStore.getAttachmentByLink(url)
  if (!isValidPreviewAttachment(previous)) return null

  return new Attachment({
    responseMediaShort: {
      content_type: previous.content_type,
      source: previous.source,
      name: previous.name,
      url: previous.source,
      source_short: url,
      size: previous.size?.toString(),
      original_source: previous.original_source,
      is_allowed_for_media_url: determineIsAllowedForMediaUrl(previous.content_type),
    },
  })
}

/**
 * Resolves attachments for the given body links: triggers a preview request for
 * unknown links, waits for it to settle, then builds `Attachment`s for the links
 * that resolved to valid attachment metadata.
 */
export const resolvePreviewAttachments = async (
  attachmentStore: AttachmentStore,
  urls: string[]
): Promise<Attachment[]> => {
  if (!urls.length) return []

  attachmentStore.checkPreviousAttachment(urls)

  const attachments: Attachment[] = []
  const stillWaiting: string[] = []

  await new Promise((resolve) => setTimeout(resolve, PREVIEW_RESOLVE_DELAYS_MS[0]))

  urls.forEach((url) => {
    const previous = attachmentStore.getAttachmentByLink(url)
    if (previous === undefined || previous === null) {
      stillWaiting.push(url)
      return
    }

    const attachment = buildAttachmentFromLink(attachmentStore, url)
    if (attachment) attachments.push(attachment)
  })

  if (stillWaiting.length > 0) {
    await new Promise((resolve) => setTimeout(resolve, PREVIEW_RESOLVE_DELAYS_MS[1]))

    stillWaiting.forEach((url) => {
      const attachment = buildAttachmentFromLink(attachmentStore, url)
      if (attachment) attachments.push(attachment)
    })
  }

  return attachments
}
